<template>
  <div
    class="product-options"
    :class="{
      [$class('loading')]: loading,
      [$class('hidden')]: product?.hasOnlyDefaultVariant && !bundle,
    }"
  >
    <div
      v-for="option of options"
      :key="option.key"
      class="product-options__option"
    >
      <legend
        v-if="!hideLabels"
        class="product-options__option-title text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
      >
        <span>{{ option.name }}</span>
      </legend>
      <div
        v-if="option.display !== 'dropdown' && option.display !== 'bundle'"
        class="product-options__values"
        :class="`product-options__grid-${option.count}`"
      >
        <template v-for="value of option.values" :key="value.key">
          <selection-tab
            v-if="!value.hidden"
            :id="`${value.key}-${uniqueId}`"
            v-model="currentOptions[option.index]"
            class="product-options__value"
            :label="value.value"
            :show-upsizing="
              option.values.filter((optionValue) => !optionValue.hidden)
                .length > 1 &&
              !value.disabled &&
              product.upsizing?.includes(value.value)
            "
            :product="product"
            :variant="variant"
            :featured-image="value.featuredImage"
            :loading="loading"
            :name="option.key"
            :hide-portions-label="hidePortionsLabel"
            :hide-sold-out-label="hideSoldOutLabel"
            :value="value.value"
            :visually-disabled="value.disabled"
            :is-ab-test="isAbTest"
            :is-esn-74-test="isEsn74Test"
            :is-esn-74-ready="isEsn74Ready"
          />
        </template>
      </div>
      <div v-else-if="option.display === 'bundle'">
        <product-bundle-select
          :option="option"
          :loading="loading"
          :product="product"
          :inline="inlineBundleOptions"
          :hide-count-label="hideCountLabel"
          @update-bundle-product="handleUpdateOption"
          @update-disabled-status="handleDisableStatus"
          @dropdown-toggled="$emit('dropdown-toggled', $event)"
          @variant-selector-namespace="
            $emit('variant-selector-namespace', $event)
          "
        />
      </div>
      <div
        v-else
        class="product-options__dropdown"
        :class="{ 'is-single': isSingleValue }"
      >
        <product-custom-select
          :select-variant="
            isFlavorSweetnessOption(option)
              ? currentCanonicalFlavor
              : currentOptions[option.index]
          "
          :options="getSelectOptions(option)"
          :aria-label="option.name"
          :count-label="hideCountLabel ? '' : option.countName"
          :disabled-group-label="$string('product.form.sold_out')"
          :placeholder="$string('product.form.select_variant')"
          :unique-id="`${option.key}-${uniqueId}`"
          :loading="loading"
          :hide-flavor-image="hideFlavorImage || isSizeOption(option.downcase)"
          :hide-flavor-image-on-button="hideFlavorImageOnButton"
          :hide-flavor-badge-on-button="hideFlavorBadgeOnButton"
          :hide-flavor-badge="hideFlavorBadge"
          :force-desktop-dropdown="forceDesktopDropdown"
          :style-full-size="styleFullSize"
          block-class="product-options"
          @update:select-variant="
            isFlavorSweetnessOption(option)
              ? handleFlavorCanonicalChange($event)
              : handleOptionChange(option.index, $event)
          "
          @dropdown-toggled="$emit('dropdown-toggled', $event)"
          @variant-selector-namespace="
            $emit('variant-selector-namespace', $event)
          "
        />
      </div>

      <segmented-control
        v-if="isFlavorSweetnessOption(option) && sweetnessSegments.length > 0"
        class="product-options__sweetness"
        :aria-label="$string('product.form.sweetness_label')"
        :model-value="currentSweetness"
        :name="`sweetness-${option.key}`"
        :options="sweetnessSegments"
        @update:model-value="handleSweetnessChange"
      />
    </div>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select"
      :class="$class('hidden')"
      name="id"
      aria-hidden
    >
      <template
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
      >
        <option
          v-if="variantObject.availableState !== 'hidden'"
          :value="variantObject.id"
          v-text="`${variantObject.title} - ${variantObject.id}`"
        />
      </template>
    </select>

    <div
      v-if="
        !inlineBundleOptions &&
        (hasExtraOptions || (product.hasOnlyDefaultVariant && bundle))
      "
      class="product-options__extra"
    >
      <div
        v-for="bundleItem of offsetBundle"
        :key="bundleItem.name"
        class="product-options__option"
      >
        <div>
          <product-bundle-select
            :option="bundleItem"
            is-extra-option
            :loading="loading"
            :product="product"
            @update-disabled-status="handleDisableStatus"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Boolean} [inlineBundleOptions] - Render bundle-type options as a
 * plain dropdown instead of a tile with its own nested overlay - required
 * when this component is already rendered inside another overlay (see
 * product-card-select.vue). Also hides the read-only "extra" bundle rows
 * (components with no selectable option, e.g. a gift item) - only relevant
 * to the full product-form UI.
 *
 * @emits updateVariant - Emit new variant object.
 * @emits dropdown-toggled - Emit isOpen value (dropdown was opened/closed).
 * @emits variant-selector-namespace - Emit overlay namespace
 *
 */
import SegmentedControl from '~async/utils/segmented-control/segmented-control';
import SelectionTab from '~async/utils/selection-tab/selection-tab';

import ProductBundleSelect from '~async/product-bundle-select/product-bundle-select.vue';
import ProductCustomSelect from '~global/product-custom-select/product-custom-select';

import { convertToHandle } from '~/helpers/convert';
import {
  isInVariation,
  triggerKameleoonEventOnce,
  waitForKameleoon,
} from '~/helpers/kameleoon';
import { emitUpdateVariant } from '~/helpers/vue';

export default {
  name: 'ProductOptions',

  components: {
    SegmentedControl,
    SelectionTab,
    ProductBundleSelect,
    ProductCustomSelect,
  },

  props: {
    bundle: {
      type: [Boolean, Object],
      default: false,
    },
    inlineBundleOptions: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
        optionStylingTile: [],
        optionStylingImageTile: [],
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variantFromUrl: {
      type: Boolean,
      default: true,
    },
    hideFlavorImage: {
      type: Boolean,
      default: false,
    },
    hideFlavorImageOnButton: {
      type: Boolean,
      default: false,
    },
    hideFlavorBadge: {
      type: Boolean,
      default: false,
    },
    hideFlavorBadgeOnButton: {
      type: Boolean,
      default: false,
    },
    hideCountLabel: {
      type: Boolean,
      default: false,
    },
    hidePortionsLabel: {
      type: Boolean,
      default: false,
    },
    hideLabels: {
      type: Boolean,
      default: true,
    },
    forceDesktopDropdown: {
      type: Boolean,
      default: false,
    },
    hideSoldOutLabel: {
      type: Boolean,
      default: false,
    },
    styleFullSize: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      default: () => `${Math.random().toString(36).substring(2, 11)}`,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'bundle-extra-item': (payload) => typeof payload === 'object',
    'update-bundle-status': (payload) => typeof payload === 'boolean',
    'dropdown-toggled': (value) => typeof value === 'boolean',
    'variant-selector-namespace': (value) => typeof value === 'string',
  },

  data() {
    return {
      currentOptions: [],
      currentVariantId: 0,
      currentCanonicalFlavor: '',
      currentSweetness: 'normal',
      bundleProducts: Array.from({ length: this.bundle + 1 }, () => ({})),
      hasExtraOptions: false,
      bundleStatus: [],
      esn45: {
        experimentId: 412618,
        activeVariation: null,
        variations: {
          1: 1369152,
        },
      },
      stopEsn45: null,
      esn74: {
        experimentId: 416100,
        experimentTriggered: false,
        experimentReady: false,
        variationId: 1377729,
      },
      stopEsn74: null,
    };
  },

  computed: {
    /**
     * Check if visitor is in Flavor Image AB test variation 1.
     * @returns {Boolean}
     */
    isEsn45Variation1() {
      return this.esn45.activeVariation === this.esn45.variations[1];
    },

    /**
     * Checks if the ESN-74 test is active.
     * @returns {Boolean}
     */
    isEsn74Test() {
      return this.esn74.experimentTriggered;
    },

    /**
     * Whether the ESN-74 experiment result has come back from Kameleoon.
     * - Used to keep the portion label hidden until it's known whether this
     *   visitor is in the variation, avoiding a flash of the label.
     * @returns {Boolean}
     */
    isEsn74Ready() {
      return this.esn74.experimentReady;
    },
    isSingleValue() {
      return this.options.every((option) => option.count === 1);
    },
    /**
     * Get offset bundle items.
     * @returns {Array}
     */
    offsetBundle() {
      const leftOverBundle =
        this.options.length === 0
          ? this.bundle
          : this.bundle.slice(this.options.length);

      const offsetBundle = [];
      for (let left = 0; left < leftOverBundle.length; left++) {
        const currentAvailableState = leftOverBundle[
          left
        ].variantsAvailability.find((available) =>
          leftOverBundle[left].variants.some(
            (variant) => available.id === variant.id,
          ),
        );

        const extraOptionMapping = {
          currentOption: leftOverBundle[left].variants[0],
          currentOptionName: leftOverBundle[left].variants[0]?.option1,
          count: 1,
          display: 'bundle',
          downcase: leftOverBundle[left].id.toLowerCase(),
          handle: convertToHandle(leftOverBundle[left].id),
          name: leftOverBundle[left].id,
          key: `${this.section.id}-${this.product.handle}-option-${left}-${convertToHandle(leftOverBundle[left].id)}`,
          availableState: currentAvailableState.availableState,
        };

        offsetBundle.push(extraOptionMapping);
        this.$emit('bundle-extra-item', offsetBundle);
      }

      return offsetBundle;
    },

    /**
     * Get extended options data.
     * @returns {Array}
     */
    options() {
      if (this.loading && (!this.product || this.product.liquid)) {
        return this.getOptionsPlaceholder();
      }

      if (
        (!this.product?.options?.length && !this.bundle) ||
        this.product?.hasOnlyDefaultVariant
      ) {
        return [];
      }

      /**
       * Create deep clone of variants to avoid dirty computed getters.
       * Note: Using JSON.parse/stringify instead of structuredClone because
       * Vue's reactive objects contain non-cloneable properties.
       */
      const variantsClone = JSON.parse(JSON.stringify(this.product.variants));

      /**
       * Get extended options data.
       */

      return this.product.options?.map((option) => {
        const handle = convertToHandle(option.name);
        let currentBundleOption = false;
        let currentBundle = false;
        let extraBundleInfo = false;

        const downcase = option.name.toLowerCase();
        const key = `${this.section.id}-${this.product.handle}-option-${option.index}-${convertToHandle(option.name)}`;
        const disabled = {};
        const hidden = {};
        let display = 'dropdown';

        if (this.section.optionStylingTile?.includes(downcase)) {
          display = 'tile';
        } else if (this.section.optionStylingImageTile?.includes(downcase)) {
          display = 'image';
        } else if (this.bundle && this.bundle.length > 0) {
          display = 'bundle';
        }

        /**
         * Setup current extended data for values updates.
         */
        const optionObject = {
          ...option,
          disabled,
          display,
          downcase,
          handle,
          hidden,
          key,
          originalValues: option.values,
        };

        const values = this.getOptionValues(optionObject, variantsClone);

        // Sort values so sold-out flavors appear at the end
        const sortedValues = values.sort((a, b) => {
          // If both are sold out or both are not sold out, maintain original order
          if (a.soldOut === b.soldOut) {
            return 0;
          }
          // Move sold out items to the end
          return a.soldOut ? 1 : -1;
        });

        let availableState = this.getSoldOutValues(
          sortedValues,
          optionObject.downcase,
        );

        let count = values.filter((value) => !value.hidden).length;
        let countName = this.getOptionCountName(count, downcase);

        if (this.bundle && this.bundle.length > 0) {
          currentBundle = this.bundle.find(
            (bundle) => bundle.id === option.name,
          );

          // for bundles logic to show products that are not part of
          // product property
          if (
            this.product?.options?.length !== this.bundle?.length ||
            (this.product?.options && this.bundle?.length > 0)
          ) {
            this.hasExtraOptions = true;
          }

          currentBundleOption = currentBundle?.variants.find(
            (current) =>
              option.labels.includes(current?.option1) &&
              current.available === true,
          );

          if (!currentBundleOption) {
            currentBundleOption = currentBundle?.variants[0];
          }

          // only available if setup in metaobject, optional values
          extraBundleInfo = {
            name: currentBundle.name,
            type: currentBundle.type,
          };

          // Bundles have no information on variant metafields
          // therefore we get them from the variants in the metaobject
          const getBundleVariantIds = new Set(
            currentBundle.variants.map((variant) => variant.id),
          );

          const filteredAvailability =
            currentBundle.variantsAvailability.filter((variant) =>
              getBundleVariantIds.has(variant.id),
            );

          availableState = filteredAvailability;

          count = currentBundle.variants.filter((variant) => {
            const availability = filteredAvailability.find(
              (state) => state.id === variant.id,
            );

            return availability?.availableState !== 'hidden';
          }).length;
          countName = this.getOptionCountName(count, downcase);
        }

        /**
         * Return extended data.
         */
        return {
          ...optionObject,
          count,
          countName,
          availableState,
          values: sortedValues,
          currentOption: currentBundleOption,
          bundle: currentBundle.variants,
          extraBundleInfo,
        };
      });
    },

    /**
     * Compute size option index.
     * @returns {Number}
     */
    sizeOptionIndex() {
      return this.options.findIndex((option) => {
        return this.$variable('product.sizeOptions').includes(
          option.name.toLowerCase(),
        );
      });
    },

    /**
     * Compute flavor option index.
     * - Reads directly from the raw `product.options` prop rather than the
     *   extended `options` computed, since `options` itself (via
     *   `getOptionValues`/`flavorCanonicalGroups`) depends on this value -
     *   referencing `options` here would be a circular computed dependency.
     * @returns {Number}
     */
    flavorOptionIndex() {
      const flavorOption = this.product?.options?.find((option) => {
        return this.$variable('product.flavourOptions').includes(
          option.name.toLowerCase(),
        );
      });

      return flavorOption ? flavorOption.index : -1;
    },

    /**
     * Check if the sweetness selector should be shown for this product.
     * - Gated by the `custom.pdp_show_sweetness_option` product metafield.
     * - Only makes sense if the product actually has a flavor option, since
     *   sweetness is resolved as a grouping of flavor option values.
     * @returns {Boolean}
     */
    sweetnessEnabled() {
      return (
        Boolean(this.product?.showSweetnessOption) &&
        this.flavorOptionIndex !== -1
      );
    },

    /**
     * Group raw flavor option values (e.g. "Lemon" / "Lemon Less Sweet")
     * into a single canonical entry per base flavor.
     * - Sweetness classification per raw value comes from the authoritative
     *   `custom.pdp_sweetness_option` variant metafield, never from parsing
     *   the flavor name.
     * - The canonical (display) name is derived by stripping a configured
     *   "less sweet" suffix (`product.lessSweetSuffixes` theme setting) for
     *   grouping/display purposes only.
     * @returns {Object} - Map of canonicalName -> { canonicalName, normal, lessSweet }.
     */
    flavorCanonicalGroups() {
      if (!this.sweetnessEnabled) {
        return {};
      }

      const flavorOption = this.product.options?.[this.flavorOptionIndex];

      if (!flavorOption) {
        return {};
      }

      const groups = {};

      for (const rawValue of flavorOption.values) {
        const canonicalName = this.getCanonicalFlavorName(rawValue);
        const sweetness = this.getRawFlavorSweetness(rawValue);
        const slot = sweetness === 'less sweet' ? 'lessSweet' : 'normal';

        if (!groups[canonicalName]) {
          groups[canonicalName] = {
            canonicalName,
            normal: null,
            lessSweet: null,
          };
        }

        /**
         * If two raw values both resolve to the same sweetness slot for a
         * canonical flavor (usually a missing/wrong custom.pdp_sweetness_option
         * metafield), the later one in `flavorOption.values` order wins and
         * the other silently disappears from the picker.
         */
        groups[canonicalName][slot] = rawValue;
      }

      return groups;
    },

    /**
     * Get the sweetness segments for the currently selected canonical
     * flavor.
     * - Always returns both "normal" and "less sweet" segments once a
     *   flavor is selected, so the control is consistently visible across
     *   all flavors. A segment is disabled only when its raw flavor value
     *   doesn't exist at all for this flavor (no real choice - the other
     *   segment stays the only active one), or when it exists but is
     *   hidden (e.g. not published to this market) - being sold out does
     *   NOT disable it. A sold-out sweetness should behave like any other
     *   sold-out option elsewhere on the PDP: still selectable, still shows
     *   its sizes, still offers Notify Me - see `isRawFlavorValueHidden()`.
     * @returns {Array}
     */
    sweetnessSegments() {
      const group = this.flavorCanonicalGroups[this.currentCanonicalFlavor];

      if (!group) {
        return [];
      }

      return [
        {
          value: 'normal',
          label: this.$string('product.form.sweetness_normal'),
          disabled: !group.normal || this.isRawFlavorValueHidden(group.normal),
        },
        {
          value: 'less sweet',
          label: this.$string('product.form.sweetness_less_sweet'),
          disabled:
            !group.lessSweet || this.isRawFlavorValueHidden(group.lessSweet),
        },
      ];
    },

    /**
     * Check if in AB test ESN-18
     */
    isAbTest() {
      const kameleoonExperiment =
        // eslint-disable-next-line
        globalThis.Kameleoon?.API?.Experiments?.getById(345129);
      return Boolean(
        kameleoonExperiment?.associatedVariation?.id === 1200651 &&
        kameleoonExperiment?.active,
      );
    },
  },

  watch: {
    /**
     * Watch current options to update current variant ID based on choices.
     */
    currentOptions: {
      handler() {
        const variantTitle = this.currentOptions.join(' / ');
        const newVariant = this.getVariantFromProperty(variantTitle, 'title');

        if (newVariant && newVariant.availableState !== 'hidden') {
          this.currentVariantId = newVariant.id;
          return;
        }

        /**
         * If previously selected size doesn't exist then try to find default
         * size value.
         * - Will only select if available.
         */
        if (
          this.currentOptions[this.sizeOptionIndex] !==
          this.$variable('product.defaultSizeValue')
        ) {
          const defaultSizeVariantTitle = this.currentOptions
            .map((currentOption, index) => {
              if (index === this.sizeOptionIndex) {
                return this.$variable('product.defaultSizeValue');
              }

              return currentOption;
            })
            .join(' / ');

          const defaultSizeVariant = this.getVariantFromProperty(
            defaultSizeVariantTitle,
            'title',
          );

          if (defaultSizeVariant && defaultSizeVariant.available) {
            this.currentVariantId = defaultSizeVariant.id;
            return;
          }
        }

        /**
         * If default size value doesn't exist in option then find variants
         * which match the first variant (excluding hidden variants).
         */
        const matchingVariants = this.product.variants
          .filter((variant) => variant.availableState !== 'hidden')
          .filter(
            (variant) =>
              variant.selectedOptions[0].value === this.currentOptions[0],
          );

        /**
         * If exact match then ignore availability and select it.
         */
        const exactMatch = matchingVariants.find((variant) => {
          return variant.selectedOptions.every((selectedOption, index) => {
            return selectedOption.value === this.currentOptions[index];
          });
        });

        if (exactMatch) {
          this.currentVariantId = exactMatch.id;
          return;
        }

        /**
         * If no exact match then use first variant if all unavailable
         * - Otherwise use first available.
         */
        const allUnavailable = matchingVariants.every(
          (variant) => !variant.available,
        );

        const fallbackVariant = allUnavailable
          ? matchingVariants[0]
          : matchingVariants.find((variant) => variant.available);

        if (!fallbackVariant) {
          return;
        }

        this.currentVariantId = fallbackVariant.id;
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      this.initEsn74();

      if (this.variant.id === this.currentVariantId) {
        return;
      }

      const variant = this.getVariantFromProperty(this.currentVariantId);

      if (!variant) {
        return;
      }

      this.$emit('update-variant', variant);
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      if (this.product.liquid) {
        return;
      }

      this.currentOptions = [];
      this.getCurrentVariant();
    },

    /**
     * Watch variant for updates to update current options.
     */
    variant() {
      if (!this.variant) {
        this.currentVariantId = 0;
      }

      this.getCurrentOptions();
    },
  },

  mounted() {
    this.getCurrentVariant();
    this.initEsn45();
    this.initEsn74();

    if (
      this.bundle &&
      this.product?.options?.length === 1 &&
      this.product.type === 'bundle'
    ) {
      this.hasExtraOptions = true;
    }
  },

  beforeUnmount() {
    this.stopEsn45?.();
    this.stopEsn74?.();
  },

  methods: {
    /**
     * Initialize Flavor Image AB test (Experiment 412618) - waits for
     * Kameleoon to load and assigns currently active variation.
     * - Stores the returned cleanup function so the poll can be stopped in
     *   beforeUnmount(), since this component is repeatedly mounted/unmounted
     *   (e.g. quick-add.vue toggles it via v-if) and would otherwise leave a
     *   dangling interval mutating a detached instance.
     */
    initEsn45() {
      this.stopEsn45 = waitForKameleoon({
        experimentId: this.esn45.experimentId,
        onReady: (experiment) => {
          for (const variation of Object.values(this.esn45.variations)) {
            if (isInVariation(this.esn45.experimentId, variation, experiment)) {
              this.esn45.activeVariation = variation;
              break;
            }
          }
        },
      });
    },

    /**
     * Initializes the ESN-74 (PDP price-per-portion) experiment.
     * - Shared across all selection-tab instances rendered by this
     *   component, so every tab agrees on whether the variation is active.
     */
    initEsn74() {
      if (this.esn74.experimentTriggered || this.hidePortionsLabel) {
        return;
      }

      this.stopEsn74?.();
      triggerKameleoonEventOnce('esn74-pdp-price-portion');
      this.stopEsn74 = waitForKameleoon({
        experimentId: this.esn74.experimentId,
        onReady: (experiment) => {
          const isTriggered =
            experiment?.triggered || experiment?.triggeredInVisit;

          if (
            isTriggered &&
            isInVariation(
              this.esn74.experimentId,
              this.esn74.variationId,
              experiment,
            )
          ) {
            this.esn74.experimentTriggered = true;
          }

          this.esn74.experimentReady = true;
        },
      });
    },

    /**
     * Resolve the flavor image for a variant, preferring the AB test
     * alternative image when the visitor is in Flavor Image AB test
     * variation 1.
     * @param {Object} variant - Canvas formatted variant object.
     * @returns {Object|Boolean}
     */
    getVariantFlavorImage(variant) {
      const alternativeImage = variant?.alternativeFlavorImage;
      const hasAlternativeImage = Boolean(
        alternativeImage?.src || alternativeImage?.url,
      );

      if (this.isEsn45Variation1 && hasAlternativeImage) {
        return alternativeImage;
      }

      return variant?.flavorImage;
    },

    /**
     * Handle update bundleItem event.
     * @param {Object} bundleItem - Emitted bundleItem object.
     * @param {Number} index - Option index.
     */
    handleUpdateOption(bundleItem, index) {
      // Guard for bundle products: Ensure bundleItem exists and has required properties
      if (!bundleItem || typeof bundleItem !== 'object') {
        if (this.bundle && this.bundle.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '[product-options] handleUpdateOption called with invalid bundleItem for bundle product',
            { bundleItem, index },
          );
        }
        return;
      }

      if (!bundleItem.option1) {
        if (this.bundle && this.bundle.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '[product-options] bundleItem missing option1 property',
            { bundleItem, index },
          );
        }
        return;
      }

      this.currentOptions[index] = bundleItem.option1;
      this.selectedVariant = this.variant;
    },

    /**
     * Get option values.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Array}
     */
    getOptionValues(option, variants) {
      if (this.sweetnessEnabled && option.index === this.flavorOptionIndex) {
        return this.getFlavorCanonicalValues(option, variants);
      }

      let values = option.values;

      if (this.$variable('product.sizeOptions').includes(option.downcase)) {
        values = this.getSortedContentSizeValues(option.values);
      } else if (
        this.$variable('product.clothingSizeOptions').includes(option.downcase)
      ) {
        values = this.getSortedSizeValues(option.values);
      }

      return values.map((value, index) => {
        const handle = convertToHandle(value);
        const downcase = value.toLowerCase();
        const key = `${option.key}-value-${index}-${handle}`;

        /**
         * Determine value attributes.
         */
        const {
          disabled,
          featuredImage,
          flavorBadge,
          flavorImage,
          hidden,
          soldOut,
        } = this.getValueAttributes(value, option, variants);

        return {
          downcase,
          disabled,
          featuredImage,
          flavorBadge,
          flavorImage,
          handle,
          hidden,
          key,
          soldOut,
          value,
        };
      });
    },

    /**
     * Check if the given option is the flavor option with sweetness grouping
     * active for this product.
     * @param {Object} option - Current extended option data.
     * @returns {Boolean}
     */
    isFlavorSweetnessOption(option) {
      return this.sweetnessEnabled && option.index === this.flavorOptionIndex;
    },

    /**
     * Strip a configured "less sweet" suffix from a raw flavor value to
     * get its canonical (display) name.
     * - Only used for grouping/display, never for sweetness classification.
     * @param {String} rawValue - Raw flavor option value.
     * @returns {String}
     */
    getCanonicalFlavorName(rawValue) {
      const suffixes = this.$variable('product.lessSweetSuffixes') || [];
      const downcaseValue = rawValue.toLowerCase();

      const matchedSuffix = suffixes.find(
        (suffix) => suffix && downcaseValue.endsWith(suffix),
      );

      if (!matchedSuffix) {
        return rawValue;
      }

      return rawValue.slice(0, rawValue.length - matchedSuffix.length).trim();
    },

    /**
     * Check whether a string names "less sweet" - either the literal
     * English value, or one of the configured `less_sweet_suffixes`
     * (default: `"Less Sweet, Weniger süß, Minder zoet"`), compared
     * trimmed and case-insensitively.
     * - Reused for both the flavor-name suffix (`getCanonicalFlavorName()`)
     *   and the `custom.pdp_sweetness_option` metafield value
     *   (`getRawFlavorSweetness()`) - see the latter for why a single
     *   language-suffix list has to cover both.
     * @param {String} value - String to check.
     * @returns {Boolean}
     */
    isLessSweetPhrase(value) {
      if (!value) {
        return false;
      }

      const suffixes = this.$variable('product.lessSweetSuffixes') || [];
      const downcaseValue = value.trim().toLowerCase();

      return suffixes.includes(downcaseValue);
    },

    /**
     * Get the sweetness classification for a raw flavor value.
     * - Primary source: the authoritative `custom.pdp_sweetness_option`
     *   variant metafield.
     * - Fallback (metafield not set on the matched variant - not yet
     *   tagged, or the variant wasn't in the loaded page, see
     *   wiki/guides/shopify-patterns.md "Connection pagination"): use the
     *   name-suffix hint instead of assuming "normal", so a forgotten
     *   metafield on an obviously "less sweet"-named value doesn't silently
     *   collide with/hide its normal counterpart in `flavorCanonicalGroups`.
     * - The metafield value is checked against the same multi-language
     *   `less_sweet_suffixes` list as the flavor name (`isLessSweetPhrase()`),
     *   not just the literal English `'less sweet'` - it's a Shopify
     *   choice-list metafield, and choice-list *values* (not just labels)
     *   can be translated per market/language (Translate & Adapt). Found
     *   via QA bug report (2026-09-25): a variant's metafield returned the
     *   German translation `"weniger süß"` even on the English storefront
     *   (a translation-setup mistake, not something we can fix here) -
     *   reusing the suffix list means that phrase is still recognised as
     *   "less sweet" instead of silently falling through to "normal".
     * @param {String} rawValue - Raw flavor option value.
     * @returns {String} - 'normal' or 'less sweet'.
     */
    getRawFlavorSweetness(rawValue) {
      const matchedVariant = this.product.variants.find((variant) => {
        return (
          variant.selectedOptions[this.flavorOptionIndex]?.value === rawValue
        );
      });

      if (matchedVariant?.sweetnessOption) {
        return this.isLessSweetPhrase(matchedVariant.sweetnessOption)
          ? 'less sweet'
          : 'normal';
      }

      return this.getCanonicalFlavorName(rawValue) === rawValue
        ? 'normal'
        : 'less sweet';
    },

    /**
     * Check if every variant for a raw flavor value is hidden.
     * - Deliberately NOT "sold out" - being out of stock should never
     *   disable a sweetness segment (the user should still be able to see
     *   its sizes and use Notify Me, same as any other sold-out option on
     *   the PDP). Only genuinely hidden variants (e.g. not published to
     *   this market) mean there's truly no real choice to offer.
     * @param {String} rawValue - Raw flavor option value.
     * @returns {Boolean}
     */
    isRawFlavorValueHidden(rawValue) {
      const matchedVariants = this.product.variants.filter((variant) => {
        return (
          variant.selectedOptions[this.flavorOptionIndex]?.value === rawValue
        );
      });

      if (!matchedVariants.length) {
        /**
         * No matching variant loaded - could genuinely mean hidden, or it
         * could mean variant pagination hasn't finished (>250 variants,
         * this raw value's variant is on a page that failed/wasn't fetched
         * yet). Only treat it as hidden once we're confident the full
         * variant list has loaded - otherwise wrongly disabling a segment
         * that's actually available is worse than briefly leaving it enabled.
         */
        return !this.product.variantsIncomplete;
      }

      return matchedVariants.every(
        (variant) => variant.availableState === 'hidden',
      );
    },

    /**
     * Get canonical (sweetness-deduped) flavor values for the flavor option.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Array}
     */
    getFlavorCanonicalValues(option, variants) {
      return Object.values(this.flavorCanonicalGroups).map((group, index) => {
        const handle = convertToHandle(group.canonicalName);
        const downcase = group.canonicalName.toLowerCase();
        const key = `${option.key}-value-${index}-${handle}`;

        const normalAttributes = group.normal
          ? this.getValueAttributes(group.normal, option, variants)
          : null;
        const lessSweetAttributes = group.lessSweet
          ? this.getValueAttributes(group.lessSweet, option, variants)
          : null;
        const attributesList = [normalAttributes, lessSweetAttributes].filter(
          Boolean,
        );
        const primaryAttributes = normalAttributes || lessSweetAttributes;

        return {
          downcase,
          disabled: attributesList.every((attributes) => attributes.disabled),
          featuredImage: primaryAttributes.featuredImage,
          flavorBadge: primaryAttributes.flavorBadge,
          flavorImage: primaryAttributes.flavorImage,
          handle,
          hidden: attributesList.every((attributes) => attributes.hidden),
          key,
          soldOut: attributesList.every((attributes) => attributes.soldOut),
          value: group.canonicalName,
        };
      });
    },

    /**
     * Resolve the raw flavor value to select for a given canonical flavor +
     * sweetness combination.
     * - Prefers the requested sweetness whenever a raw value exists for it
     *   at all, regardless of stock status - being sold out is never a
     *   reason to silently redirect to the other sweetness (or to a
     *   different one than the user explicitly picked); it should behave
     *   like any other sold-out option elsewhere on the PDP: still
     *   selected, still showing its sizes, still offering Notify Me.
     * - Only falls back to the other slot when the requested sweetness has
     *   no raw value at all for this flavor (a genuine absence, not a
     *   stock issue).
     * @param {String} canonicalName - Canonical flavor name.
     * @param {String} sweetness - 'normal' or 'less sweet'.
     * @returns {Object} - `{ rawValue, sweetness }` of what was actually selected.
     */
    resolveFlavorSweetnessSelection(canonicalName, sweetness) {
      const group = this.flavorCanonicalGroups[canonicalName];

      if (!group) {
        return { rawValue: '', sweetness: 'normal' };
      }

      if (sweetness === 'less sweet' && group.lessSweet) {
        return { rawValue: group.lessSweet, sweetness: 'less sweet' };
      }

      if (sweetness === 'normal' && group.normal) {
        return { rawValue: group.normal, sweetness: 'normal' };
      }

      /**
       * Requested sweetness doesn't exist for this flavor at all - fall
       * back to whichever one does (preferring 'normal').
       */
      if (group.normal) {
        return { rawValue: group.normal, sweetness: 'normal' };
      }

      return { rawValue: group.lessSweet, sweetness: 'less sweet' };
    },

    /**
     * Handle sweetness segment change.
     * @param {String} value - 'normal' or 'less sweet'.
     */
    handleSweetnessChange(value) {
      const { rawValue, sweetness } = this.resolveFlavorSweetnessSelection(
        this.currentCanonicalFlavor,
        value,
      );

      if (!rawValue) {
        return;
      }

      this.currentSweetness = sweetness;
      this.currentOptions[this.flavorOptionIndex] = rawValue;
    },

    /**
     * Handle canonical flavor change from the flavor dropdown.
     * - Keeps the current sweetness preference when the new flavor supports
     *   it, otherwise defaults to 'normal', otherwise auto-selects whichever
     *   sweetness variant actually exists.
     * @param {String} canonicalName - Canonical flavor name.
     */
    handleFlavorCanonicalChange(canonicalName) {
      const group = this.flavorCanonicalGroups[canonicalName];
      const sweetnessKey =
        this.currentSweetness === 'less sweet' ? 'lessSweet' : 'normal';
      const keepsCurrentSweetness = Boolean(group?.[sweetnessKey]);
      const preferredSweetness = keepsCurrentSweetness
        ? this.currentSweetness
        : 'normal';

      const { rawValue, sweetness } = this.resolveFlavorSweetnessSelection(
        canonicalName,
        preferredSweetness,
      );

      if (!rawValue) {
        return;
      }

      this.currentCanonicalFlavor = canonicalName;
      this.currentSweetness = sweetness;
      this.currentOptions[this.flavorOptionIndex] = rawValue;
    },

    /**
     * Get current value object.
     * @param {String} value - Value object of search option
     * @returns {Array} - Array of all matching value objects across all options.
     */
    getCurrentValue(value) {
      const matchingValues = this.options.flatMap((opt) =>
        opt.values.filter((val) => val.value === value),
      );

      return matchingValues;
    },

    /**
     * check if option value is sold out for selected option
     * - only if all variants are sold out
     * @param {String} value - option value
     * @returns {Boolean}
     */
    isSoldOutOption(value) {
      const matchingValues = this.getCurrentValue(value);
      if (!matchingValues || matchingValues.length === 0) {
        return false;
      }
      return matchingValues.every((val) => val.soldOut);
    },

    getFlavorImage(value) {
      const matchingValues = this.getCurrentValue(value);

      if (!matchingValues || matchingValues.length === 0) {
        return null;
      }

      // Check if flavorImage is valid (not empty object)
      const isValidImage = (image) => {
        return (
          image &&
          typeof image === 'object' &&
          Object.keys(image).length > 0 &&
          (image.src || image.url)
        );
      };

      // First, try to use current variant's flavorImage if it matches this value
      const isCurrentFlavorValue =
        this.variant?.selectedOptions?.[0]?.value === value;
      const currentFlavorImage = this.getVariantFlavorImage(this.variant);

      if (isCurrentFlavorValue && isValidImage(currentFlavorImage)) {
        return currentFlavorImage;
      }

      // Fallback: find any matching value that has a valid image
      const valueWithImage = matchingValues.find((val) =>
        isValidImage(val.flavorImage),
      );

      return valueWithImage?.flavorImage || null;
    },

    /**
     * Get flavor badge for current option value
     * @param {String} value - option value
     * @returns {String|Boolean}
     */
    getFlavorBadge(value) {
      const matchingValues = this.getCurrentValue(value);
      if (!matchingValues || matchingValues.length === 0) {
        return false;
      }

      // Find first value that has a flavorBadge
      const valueWithBadge = matchingValues.find((val) => val.flavorBadge);
      return valueWithBadge?.flavorBadge || false;
    },

    /**
     * Check if option handle is a size option.
     * @param {String} optionDowncase - option name lowercase.
     * @returns {Boolean}
     */
    isSizeOption(optionDowncase) {
      return this.$variable('product.sizeOptions').includes(optionDowncase);
    },

    /**
     * Create custom sort function for values based on sorting array.
     * @param {Array<String>} sortingArray - Array defining sort order.
     * @returns {Function}
     */
    createCustomSort(sortingArray) {
      return (a, b) => {
        const indexA = sortingArray.indexOf(a.toLowerCase());
        const indexB = sortingArray.indexOf(b.toLowerCase());

        return indexA === -1 && indexB === -1
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : indexA - indexB;
      };
    },

    /**
     * Get custom sorted content size values.
     * - Before they're objects.
     * @param {Array<String>} values - Option values.
     * @returns {Array}
     */
    getSortedContentSizeValues(values) {
      const customSort = this.createCustomSort(
        this.$variable('product.variantSorting'),
      );
      return [...values].sort(customSort);
    },

    /**
     * Get custom sorted size (clothing) values.
     * - Before they're objects.
     * @param {Array<String>} values - Option values.
     * @returns {Array}
     */
    getSortedSizeValues(values) {
      const customSort = this.createCustomSort(
        this.$variable('product.sizeOptionSorting'),
      );
      return [...values].sort(customSort);
    },
    /**
     * Get additional option value attributes based on product filters.
     * - First find variants that match current value.
     * @param {String} value - Current value.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Object}
     */
    getValueAttributes(value, option, variants) {
      const matchedIndexVariants = variants.filter((variant) => {
        return variant.selectedOptions[option.index].value === value;
      });

      /**
       * Use matched index variant to find top level attributes.
       */
      const soldOut =
        option.index === 0
          ? matchedIndexVariants
              .filter((variant) => variant.availableState !== 'hidden')
              .every((variant) => !variant.available)
          : false;

      const featuredImage =
        option.display === 'image' ? matchedIndexVariants[0].featuredImage : {};

      // Find first variant with a flavorImage, fallback to first variant
      const variantWithFlavorImage =
        matchedIndexVariants.find((variant) => {
          const image = this.getVariantFlavorImage(variant);
          return image && (image.src || image.url);
        }) || matchedIndexVariants[0];

      const flavorImage =
        this.getVariantFlavorImage(variantWithFlavorImage) || false;

      // Find first variant with a flavorBadge
      const variantWithFlavorBadge = matchedIndexVariants.find(
        (variant) => variant.flavorBadge,
      );
      const flavorBadge = variantWithFlavorBadge?.flavorBadge || false;

      /**
       * Find variants that match all other currentOptions.
       */
      const matchedVariants = matchedIndexVariants.filter((variant) => {
        if (option.index === 0) {
          return true;
        }

        return this.currentOptions.every(
          (currentOption, currentOptionIndex) => {
            if (!currentOption || option.index === currentOptionIndex) {
              return true;
            }

            return (
              variant.selectedOptions[currentOptionIndex]?.value ===
              currentOption
            );
          },
        );
      });

      return {
        disabled: matchedVariants.every((variant) => !variant.available),
        flavorBadge,
        flavorImage,
        featuredImage,
        hidden: matchedVariants.every(
          (variant) => variant.availableState === 'hidden',
        ),
        soldOut,
      };
    },

    /**
     * Get sold out values.
     * - Sort by size sorting if applicable.
     * @param {Array<Object>} values - Option values.
     * @param {String} option - Downcased option name.
     * @returns {Array}
     */
    getSoldOutValues(values, option) {
      const availableState = values.filter((value) => {
        return value.soldOut && !value.hidden;
      });

      if (!availableState.length) {
        return [];
      }

      const customSort = (a, b) => {
        if (this.$variable('product.clothingSizeOptions').includes(option)) {
          const indexA = this.$variable('product.sizeOptionSorting').indexOf(
            a.downcase,
          );
          const indexB = this.$variable('product.sizeOptionSorting').indexOf(
            b.downcase,
          );

          return indexA === -1 && indexB === -1
            ? a.downcase.localeCompare(b.downcase)
            : indexA - indexB;
        }

        return a.downcase.localeCompare(b.downcase);
      };

      return availableState.sort(customSort);
    },

    /**
     * Handles status from bundle products
     * - if one is soldout/hidden/not available we receive a boolean
     *  @param {Boolean} status - Status of bundle product
     */
    handleDisableStatus(status) {
      this.bundleStatus.push(status);
      const isDisabled = this.bundleStatus.some((state) => state === true);

      /**
       * when one of the values is true
       */
      if (isDisabled) {
        this.$emit('update-bundle-status', true);
      }
    },

    /**
     * Compute option count name depending on option handle.
     * @param {Number} count - Number of products.
     * @param {String} optionDowncase - Option name downcased.
     * @returns {String}
     */
    getOptionCountName(count, optionDowncase) {
      const options = {
        pluralise: count,
        replace: { count },
      };

      if (this.$variable('product.sizeOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count_size', options);
      }

      if (this.$variable('product.flavourOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count', options);
      }

      if (this.$variable('product.colorOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count_color', options);
      }

      return this.$string('product.variant.option_count', options);
    },

    /**
     * Get options placeholder when loading with no existing product data.
     * - Liquid product data doesn't count as it's possibly in the wrong order.
     * @returns {Array}
     */
    getOptionsPlaceholder() {
      if (this.product?.hasOnlyDefaultVariant) {
        return [];
      }

      const hasImageTile = this.product.options.some((option) => {
        return this.section.optionStylingImageTile.includes(
          option.name.toLowerCase(),
        );
      });

      const tileType = hasImageTile ? 'image' : 'tile';

      return Array.from({ length: 2 }, (_, index) => {
        const name = `${this.$string('accessibility.loading')} ${index}`;

        const option = {
          index,
          labels: Array.from(
            { length: 2 },
            (__, labelIndex) => `${name} ${labelIndex}`,
          ),
          name,
          values: Array.from(
            { length: 2 },
            (__, valueIndex) => `${name} ${valueIndex}`,
          ),
        };

        const handle = convertToHandle(option.name);
        const key = `${handle}-key`;

        const values = Array.from({ length: 2 }, (__, valueIndex) => {
          const value = `${this.$string('accessibility.loading')} ${valueIndex}`;
          const valueHandle = convertToHandle(value);
          const valueDowncase = value.toLowerCase();

          return {
            downcase: valueDowncase,
            disabled: false,
            featuredImage: tileType === 'image' ? {} : false,
            handle: valueHandle,
            hidden: false,
            key: valueHandle,
            soldOut: false,
            value,
          };
        });

        return {
          ...option,
          count: 2,
          countName: '',
          disabled: {},
          display: index === 0 ? 'dropdown' : tileType,
          downcase: '',
          handle,
          hidden: {},
          key,
          originalValues: values,
          availableState: [],
          validValues: values,
          values,
        };
      });
    },

    /**
     * Get variant key.
     * @param {Object} variant - Current variant.
     * @param {Number} index - Current index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product?.handle}-option-${index}-${convertToHandle(variant?.title)}`;
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (
        !this.product ||
        !this.product?.variants?.length ||
        this.product.liquid
      ) {
        return;
      }

      /**
       * Always get current variant from URL.
       * - The URL's variant id may not belong to this product at all (e.g.
       *   a stale `?variant=` param from a different product) — in that case
       *   fall through to the sensible-default logic below instead of
       *   crashing on an unresolved variant.
       */
      const variantFromUrl = this.getVariantFromUrl();
      if (variantFromUrl && this.variantFromUrl) {
        const availableVariant = this.checkAvailability(variantFromUrl.id);

        if (availableVariant) {
          this.currentVariantId = availableVariant.id;
          this.getCurrentOptions();
          return;
        }
      }

      if (!this.variantFromUrl && this.variant) {
        this.currentVariantId = this.variant.id;
        this.getCurrentOptions();
        return;
      }

      /**
       * Pick the first in stock variant, which is available.
       * - If product has size option then select first available 908g variant.
       * - If no variants are in stock then use first variant.
       */
      let validVariants = this.product?.variants?.filter(
        (variant) =>
          variant.available && variant.availableState === 'available',
      );

      if (this.sizeOptionIndex) {
        validVariants = this.product?.variants?.filter((variant) => {
          return (
            variant.available ||
            variant.selectedOptions[this.sizeOptionIndex]?.value ===
              this.$variable('product.defaultSizeValue')
          );
        });
      }

      /**
       * If no valid variants then select first non-hidden variant.
       * - If a product has no non-hidden variants then it should be hidden.
       */
      let currentVariant =
        this.product?.variants.find(
          (variant) => variant.availableState !== 'hidden',
        ) ?? this.product?.variants[0];

      if (validVariants.length > 0) {
        currentVariant = this.checkAvailability(validVariants[0].id);

        if (this.product.type === 'bundle') {
          currentVariant = validVariants[0];
        }
      }

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       * - Except where excluded option has only a single value, otherwise if
       *   single value options are not shown the variant will not be
       *   selectable by the user.
       */
      const currentOptions = currentVariant?.selectedOptions.map(
        (selectedOption) => {
          const optionHandle = convertToHandle(selectedOption.name);
          const matchedOption = this.options.find(
            (option) => option.handle === optionHandle,
          );

          const excludedOption =
            this.$variable('product.excludedInitialSelectOptions').includes(
              selectedOption.name,
            ) && matchedOption.values.length > 1;

          return excludedOption ? '' : selectedOption.value;
        },
      );

      const filteredCurrentOptions = currentOptions.filter(Boolean);

      if (
        filteredCurrentOptions?.length !== currentVariant.selectedOptions.length
      ) {
        this.currentOptions = currentOptions;
        return;
      }

      this.currentVariantId = currentVariant?.id;
      this.getCurrentOptions();
    },

    /**
     * Check availability of the variant.
     * - if it's not available, we select the first available variant
     * @param {Number} variantId - id of the variant from the url
     */
    checkAvailability(variantId) {
      const getCurrentVariant = this.product.variants.find(
        (variant) => variant.id === variantId,
      );

      if (
        getCurrentVariant &&
        getCurrentVariant?.availableState === 'available' &&
        getCurrentVariant?.available
      ) {
        return getCurrentVariant;
      }

      if (getCurrentVariant?.availableState === 'hidden') {
        return this.product?.variants.find(
          (variant) => variant.availableState !== 'hidden',
        );
      }

      const allVariantsUnavailable = this.product?.variants.every(
        (variant) =>
          variant.available === false ||
          variant.availableState === 'hidden' ||
          variant.availableState === 'sold-out',
      );

      if (allVariantsUnavailable) {
        return getCurrentVariant;
      }

      // Fall back to the requested variant if none of the product's other
      // variants are in a plain 'available' state either (e.g. 'back-soon').
      return (
        this.product?.variants.find(
          (variant) =>
            variant.availableState === 'available' && variant.available,
        ) ?? getCurrentVariant
      );
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1));
      const variantId = Number(params.get('variant'));
      if (location.pathname.includes('products_preview')) {
        return this.getVariantFromProperty(
          this.product.selectedOrFirstAvailableVariant.id,
        );
      }

      return this.getVariantFromProperty(variantId);
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object}
     */
    getVariantFromProperty(value, property = 'id') {
      return this.product.variants?.find((variant) => {
        return variant[property] === value;
      });
    },

    /**
     * Get current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        this.currentOptions = [];
        return;
      }

      for (const [
        index,
        selectedOption,
      ] of this.variant.selectedOptions.entries()) {
        this.currentOptions[index] = selectedOption.value;
      }

      if (this.sweetnessEnabled) {
        const rawFlavorValue = this.currentOptions[this.flavorOptionIndex];
        this.currentCanonicalFlavor =
          this.getCanonicalFlavorName(rawFlavorValue);
        // Reuse getRawFlavorSweetness()'s metafield-first/name-fallback
        // logic, so this always agrees with flavorCanonicalGroups for the
        // same raw value - reading `variant.sweetnessOption` directly here
        // would skip the name-suffix fallback and could highlight the
        // wrong segment on initial load.
        this.currentSweetness = this.getRawFlavorSweetness(rawFlavorValue);
      }
    },

    /**
     * Get select options for product-custom-select component.
     * @param {Object} option - Current option object.
     * @returns {Array}
     */
    getSelectOptions(option) {
      const options = [];

      // Add available options
      option.values.forEach((value) => {
        if (!value.hidden && !value.soldOut) {
          options.push({
            key: value.key,
            label: value.value,
            value: value.value,
            disabled: false,
            flavorImage: value.flavorImage || null,
            flavorBadge: value.flavorBadge || null,
          });
        }
      });

      // Add disabled (sold out) options
      option.values.forEach((value) => {
        if (!value.hidden && value.soldOut) {
          options.push({
            key: value.key,
            label: value.value,
            value: value.value,
            disabled: true,
            flavorImage: value.flavorImage || null,
            flavorBadge: value.flavorBadge || null,
          });
        }
      });

      return options;
    },

    /**
     * Handle option change from custom-select.
     * @param {Number} index - Option index.
     * @param {String} value - New value.
     */
    handleOptionChange(index, value) {
      this.currentOptions[index] = value;
    },
  },
};
</script>

<style lang="scss">
@import './product-options';
</style>
